/* eslint-disable import/no-cycle */
import { Button, Dropdown } from "@kitbird/core";
import React, { useMemo } from "react";
import {
  Control, Controller, FieldArrayPath, FieldArrayWithId, FieldValues, Path,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { IGetAreasResponse } from "../../../../../services/areaService";
import { IGetMasterDepartmentResponse } from "../../../../../services/masterDepartmentService";
import { IGetPoolsResponse } from "../../../../../services/poolService";
import { IJobTitleList } from "../../../../../services/masterData/jobTitle";
import { IGetSegmentsResponse } from "../../../../../services/segmentService";
import { PlacementFields } from "../CreateUserModal";
import { GetListResponseRolesType } from "../../../../../services/common/types";
import { ReactComponent as TrashIcon } from "../../../../../assets/icons/trash_Icon.svg";

const UserInputDropdown = <T extends FieldValues>({
  control,
  name,
  options,
  id,
  onSelectedChange,
  required,
  disabled,
  label,
}: {
  control: Control<T>;
  required?: boolean;
  disabled?: boolean;
  onSelectedChange?: (val: { label: string; value: string | number}) => void;
  name: Path<T>
  options: { label: string; value: string }[];
  id: string;
  label: string;
}) => <Controller
    control={control}
    name={name}
    render={({
      field: {
        onChange, value, name: nameform, ref: refForm,
      }, fieldState: { error },
    }) => (
      <Dropdown
        allowSearch
        required={required}
        id={id}
        name={nameform}
        label={label}
        disabled={disabled}
        options={options}
        onSelected={(val) => {
          onChange(val);
          if (onSelectedChange) onSelectedChange(val);
        }}
        value={value.label as string}
        error={error ? { message: error.message as string } : undefined}
        ref={refForm}
      />
    )}
  />;

const UserPositionMultipleForm = ({
  control,
  hideButton,
  className,
  index,
  item,
  listAll,
  setValue,
  remove,
}: {
    control: Control<PlacementFields>;
    getValues: UseFormGetValues<PlacementFields>;
    hideButton: boolean;
    className?: string;
    index: number;
    setValue?: UseFormSetValue<PlacementFields>;
    remove: (val: number) => void;
    item: FieldArrayWithId<PlacementFields, FieldArrayPath<PlacementFields>, "id">
    listAll: {
      listArea?: IGetAreasResponse,
      listDepartment?: IGetMasterDepartmentResponse,
      listPool?: IGetPoolsResponse,
      listJobTitle?: IJobTitleList,
      listSegments?: IGetSegmentsResponse,
      listSegment: { label: string; value: string }[]
      listRoles?: GetListResponseRolesType,
    }
}) => {
  const {
    listArea, listDepartment, listPool, listJobTitle, listSegments, listRoles, listSegment,
  } = listAll;
  const [selectedArea, selectedType, selectedPool] = useWatch({
    control,
    name: [`placement.${index}.area`, "type_code", `placement.${index}.pool`],
  });

  const optionsAreas = useMemo(() => {
    if (listArea?.listareaall && listArea?.listareaall.length > 0) {
      const templist = listArea.listareaall.map((val) => ({
        label: val.name,
        value: val.id,
      }));

      return templist;
    }
    return [];
  }, [listArea]);

  const optionsPool = useMemo(() => {
    if (listPool?.list_pool && listPool.list_pool.length > 0 && selectedArea) {
      const tempList = listPool.list_pool.filter((val) => val.area_id === selectedArea.value);
      return tempList.map((val) => ({ label: `${val.pool_id} - ${val.pool_name}`, value: val.pool_id }));
    }
    return [];
  }, [listPool, selectedArea]);

  const optionsPosition = useMemo(() => {
    if (listJobTitle?.lstsmplmstjobtitle
        && listJobTitle.lstsmplmstjobtitle.length > 0
        && selectedType) {
      const tempList = listJobTitle.lstsmplmstjobtitle
        .filter((val) => val.code_type === selectedType);
      return tempList.map((val) => ({ label: val.job_title, value: val.job_title }));
    }
    return [];
  }, [listJobTitle, selectedType]);

  const optionsDepartmen = useMemo(() => {
    if (listDepartment?.lstmstdepartment
        && listDepartment.lstmstdepartment.length > 0
        && selectedType) {
      const tempList = listDepartment.lstmstdepartment
        .filter((val) => val.code_type === selectedType);
      return tempList.map((val) => ({ label: val.department, value: val.department }));
    }
    return [];
  }, [listDepartment, selectedType]);

  const optionsRoles = useMemo(() => {
    if (listRoles?.data.items && listRoles.data.items.length > 0) {
      const tempList = listRoles.data.items.map((val) => ({ label: val.name, value: val.name }));
      return tempList;
    }
    return [];
  }, []);

  const optionsSegment = useMemo(() => {
    if (listSegments?.listSegment && listSegments.listSegment.length > 0) {
      return listSegments.listSegment
        .map((val) => ({ label: val.code_description, value: val.code_segment }));
    }

    return listSegment;
  }, [listSegments]);

  return (
    <>
      <div key={item.id} className={className}>
        <div className="user-placement-grid">
          <UserInputDropdown
            id={`area-${index}`}
            control={control}
            onSelectedChange={() => {
              if (selectedPool && setValue) setValue(`placement.${index}.pool`, { label: "", value: "" });
            }}
            name={`placement.${index}.area`}
            label="Area"
            options={optionsAreas}
          />
          <UserInputDropdown
            id={`pool-${index}`}
            control={control}
            name={`placement.${index}.pool`}
            disabled={selectedArea.value === ""}
            label="Pool"
            options={optionsPool}
          />
          <UserInputDropdown
            id={`department-${index}`}
            control={control}
            name={`placement.${index}.department`}
            disabled={!selectedType}
            label="Departemen"
            options={optionsDepartmen}
          />
          <UserInputDropdown
            id={`position-${index}`}
            control={control}
            name={`placement.${index}.position`}
            disabled={!selectedType}
            label="Posisi"
            options={optionsPosition}
          />
          <UserInputDropdown
            id={`segment-${index}`}
            control={control}
            disabled={!selectedType}
            name={`placement.${index}.segment`}
            label="Segmen"
            options={optionsSegment}
          />
          <UserInputDropdown
            id={`roles-${index}`}
            control={control}
            name={`placement.${index}.roles`}
            label="Roles"
            options={optionsRoles}
          />
        </div>
        {
          hideButton ? null : (
            <Button
              type="button"
              variant="secondary"
              color="#BE321E"
              leftIcon={<TrashIcon />}
              onClick={() => remove(index)}
              style={{
                marginTop: "1rem",
                width: "100%",
                color: "#BE321E",
                borderColor: "#BE321E",
              }}
            >
              Hapus
            </Button>
          )
        }
      </div>
    </>
  );
};

export default UserPositionMultipleForm;
